import React from 'react';

/**
 * Reusable Watch/Watching toggle. `icon` variant is a round star button for image
 * overlays (sale grid); `full` variant is a labeled pill. Stops event propagation
 * so it never triggers the card's link/bid handlers.
 */
interface WatchButtonProps {
  watched: boolean;
  onToggle: () => void;
  variant?: 'icon' | 'full';
  className?: string;
}

const WatchButton: React.FC<WatchButtonProps> = ({ watched, onToggle, variant = 'full', className = '' }) => {
  const handle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle();
  };

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={handle}
        aria-pressed={watched}
        aria-label={watched ? 'Watching — remove from watchlist' : 'Add to watchlist'}
        title={watched ? 'Watching' : 'Watch'}
        className={`flex items-center justify-center w-9 h-9 rounded-full shadow border text-lg leading-none transition ${
          watched
            ? 'bg-amber-400 border-amber-500 text-white'
            : 'bg-white/90 border-gray-300 text-gray-500 hover:text-amber-500'
        } ${className}`}
      >
        {watched ? '★' : '☆'}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handle}
      aria-pressed={watched}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-sm font-semibold border transition ${
        watched
          ? 'bg-amber-100 border-amber-400 text-amber-800'
          : 'bg-white border-gray-300 text-gray-600 hover:border-amber-400 hover:text-amber-700'
      } ${className}`}
    >
      <span>{watched ? '★' : '☆'}</span>
      {watched ? 'Watching' : 'Watch'}
    </button>
  );
};

export default WatchButton;
